import { Box, Stack, styled, TextField, Typography, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import queryString from 'query-string';
import React from 'react';
import { AlertCircle, Download } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';
import IconFeather from 'src/components/common/Icon';
import ModalComponent from 'src/components/Modal/ModalComponent';

// Utilities
import { isUndefined } from 'lodash';
import IconCredit from 'src/asset/icons/icon-credit.svg';
import { CreditCoinIcon } from 'src/components/Icons';
import { useGetPointsFeature, useGetRemainCredit } from 'src/hooks/useGetPointsFeature';
import { useQueryParams } from 'src/hooks/useQueryParam';
import useRefetch from 'src/hooks/useRefetch';
import { useMutateExportPeopleList } from 'src/services/mutate';
import { QUERY_KEY_USAGE } from 'src/services/queries';
import { LIMIT_EXPORT_PEOPLE_PER_PAGE } from 'src/utils/constants/config';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import { getFormItemError, renderFormItemError } from 'src/utils/helpers/renderFormItemError';
import { NotifyType, useNotifyCreditCosts } from 'src/zustand/useNotifyCreditCosts';
import useSnackbarStore from 'src/zustand/useSnackbarStore';

interface IProps {
  open: boolean;
  handleClose: () => void;
}

export const StyledHeaderAlert = styled(Stack)(({ theme }) => ({
  width: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  padding: '8px 32px',
}));

const PopupExportPeople: React.FC<IProps> = ({ open, handleClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const query = useQueryParams();
  const { handleOpenNotify } = useNotifyCreditCosts((state) => state);
  const { mutateAsync, isPending } = useMutateExportPeopleList();
  const pointsPrintPeople = useGetPointsFeature(LIST_FEATURES.PEOPLE_SEARCH_OVERVIEW, LIST_ACTIONS.DOWNLOAD);
  const remainCredit = useGetRemainCredit();
  const { refetchQuery } = useRefetch();
  const { dispatch: dispatchSnackbar } = useSnackbarStore();

  const formik = useFormik<ExpectedAny>({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      page_from: 1,
      page_to: 1,
    },
    validationSchema: Yup.object().shape({
      page_from: Yup.number()
        .min(1)
        .required('This field is required')
        .test('page_from_valid', "Page from cann't larger than page to", (text?: number, context?: ExpectedAny) => {
          const pageTo = context?.parent?.page_to || 0;
          const pageFrom = text || 0;
          return pageFrom <= pageTo;
        })
        .test(
          'page_from_limit',
          'You can only export a maximum of 10 pages at a time',
          (text?: number, context?: ExpectedAny) => {
            const pageTo = context?.parent?.page_to || 0;
            const pageFrom = text || 0;
            return pageTo - pageFrom < 10;
          },
        ),
      page_to: Yup.number()
        .min(1)
        .required('This field is required')
        .test('page_to_valid', "Page from cann't larger than page to", (text?: number, context?: ExpectedAny) => {
          const pageFrom = context?.parent?.page_from || 1;
          const pageTo = text || 1;
          return pageFrom <= pageTo;
        })
        .test(
          'page_to_limit',
          'You can only export a maximum of 10 pages at a time',
          (text?: number, context?: ExpectedAny) => {
            const pageFrom = context?.parent?.page_from || 1;
            const pageTo = text || 1;
            return pageTo - pageFrom < 10;
          },
        ),
    }),
    onSubmit: async (values) => {
      const response = await mutateAsync(
        queryString.stringify({ ...query, limit: getTotalRecord(), offset: values.page_from - 1 }),
      );

      if (response.status !== SUCCESS_CODE.GET) {
        dispatchSnackbar({
          open: true,
          title: 'Error',
          description: 'Export fail. Please try again.',
          variant: 'standard',
          color: 'error',
        });
        return;
      }

      // Tạo URL từ `Blob`
      const url = URL.createObjectURL(response?.data);

      // Tạo một thẻ `<a>` tạm thời để kích hoạt tải file
      const a = document.createElement('a');
      a.href = url;
      a.download = 'people_data.xlsx'; // Tên file CSV được tải về

      // Thêm thẻ `a` vào DOM và kích hoạt sự kiện click
      document.body.appendChild(a);
      a.click();

      // Sau khi tải xong, xóa thẻ `a` và URL object
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      handleClose();
      refetchQuery([QUERY_KEY_USAGE]);
    },
  });

  const handleExport = () => {
    // if (isUndefined(pointsPrintPeople?.planName)) {
    if (isUndefined(pointsPrintPeople?.planName) || getTotalCredits() > remainCredit) {
      handleOpenNotify({
        type: NotifyType.NotEnoughCredit,
        showCloseIcon: true,
      });
      return;
    }

    formik.submitForm();
  };

  const getTotalCredits = () => {
    if (formik.values.page_to >= formik.values.page_from) {
      return (formik.values.page_to - formik.values.page_from + 1) * pointsPrintPeople?.creditCosts;
    }
    return 0;
  };

  const getTotalRecord = () => {
    if (formik.values.page_to >= formik.values.page_from) {
      return LIMIT_EXPORT_PEOPLE_PER_PAGE * (formik.values.page_to - formik.values.page_from + 1);
    }
    return 0;
  };

  return (
    <ModalComponent
      padding={4}
      pt={6}
      handleClose={handleClose}
      open={open}
      closeOnBackdropClick={false}
      disabledCloseIcon={formik.isSubmitting}
      showCloseIcon={true}
      headerAlert={
        pointsPrintPeople?.planName ? (
          <StyledHeaderAlert
            id="form-contribute-header-alert"
            direction="row"
            alignItems="center"
            bgcolor={theme.palette.info.light}
          >
            <IconFeather
              icon={<AlertCircle />}
              muiProps={{
                color: 'info',
                fontSize: 'small',
              }}
            />
            <Typography variant="caption2" color="info.main" ml={1}>
              The export action can take up to 1 minute, thank you for your patience.
            </Typography>
          </StyledHeaderAlert>
        ) : (
          <StyledHeaderAlert
            id="form-contribute-header-alert"
            direction="row"
            alignItems="center"
            bgcolor={theme.palette.success.light}
          >
            <Box src={IconCredit} component="img" width={24} />
            <Typography variant="caption2" ml={1}>
              You do not have credits yet.{' '}
              <Typography
                variant="caption2"
                component="span"
                color="secondary.main"
                fontWeight={600}
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(AuthRouteKeys.Pricing)}
              >
                Please choose a suitable Plan.
              </Typography>
            </Typography>
          </StyledHeaderAlert>
        )
      }
    >
      <FormikForm formik={formik}>
        <Box width={540}>
          <Typography textAlign="center" variant="h5">
            Export Option
          </Typography>
          <Typography textAlign="center" color={theme.palette.text.secondary} variant="body2">
            Please select the page number range you'd like to export. Each page contains 50 profiles. You can export a
            maximum of 10 pages at a time.
          </Typography>
        </Box>
        <Stack direction="column" spacing={2} mt={4}>
          <Stack direction="row" spacing={2}>
            <Box width="100%">
              <TextField
                value={formik.values.page_from}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(getFormItemError('page_from', formik))}
                id="page_from"
                name="page_from"
                size="small"
                placeholder="Page From"
                label="Page From"
                type="number"
                required
                fullWidth
              />
              {renderFormItemError(getFormItemError('page_from', formik))}
            </Box>
            <Box width="100%">
              <TextField
                value={formik.values.page_to}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(getFormItemError('page_to', formik))}
                id="page_to"
                name="page_to"
                size="small"
                placeholder="To"
                label="To"
                type="number"
                required
                fullWidth
              />
              {renderFormItemError(getFormItemError('page_to', formik))}
            </Box>
          </Stack>
          <Box>
            <Typography variant="subtitle2" align="center">
              Total Records To Export
            </Typography>
            <Typography variant="h4" align="center" color="primary.main" mt={1}>
              {getTotalRecord()}
            </Typography>
          </Box>
          <ButtonWithLoading
            variant="contained"
            color="primary"
            isRounded
            size="large"
            onClick={handleExport}
            disabled={isPending || !formik.isValid}
            isLoading={isPending}
            endIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<Download />} />}
            startIcon={
              pointsPrintPeople?.planName ? (
                <Stack
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={0.5}
                  py={0.5}
                  px={1}
                  borderRadius={theme.shape.borderRadius}
                  bgcolor="rgba(255, 255, 255, 0.12)"
                >
                  <Typography variant="subtitle2" component="span" color="white">
                    {getTotalCredits()}
                  </Typography>
                  <CreditCoinIcon fontSize="small" />
                </Stack>
              ) : (
                <CreditCoinIcon fontSize="small" />
              )
            }
          >
            EXPORT TO CSV
          </ButtonWithLoading>
        </Stack>
      </FormikForm>
    </ModalComponent>
  );
};
export default PopupExportPeople;
